import { useSortable } from "@dnd-kit/react/sortable";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    // Padding rather than margin: it is the divider's own hit area, and a drop
    // just above or below the line has to land on this element to count.
    padding: "8px 0",
    color: theme.colors.staticFgNote,
  },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      backgroundColor: theme.colors.neutral030,
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/** The sortable id the divider registers under. */
export const ARCHIVED_DIVIDER_ROW_ID = "archived-divider";

interface SingleSelectArchivedDividerProps {
  index: number;
}

/**
 * The line between the active and archived options -- and a real entry in the
 * same sortable list, not decoration drawn between two lists.
 *
 * That is what makes an option's section unambiguous: a row is archived when it
 * sits below this entry, so dropping a row on either side of the line decides
 * its state, with no index arithmetic and no tie to break when a drop lands on
 * the boundary itself.
 *
 * It is droppable but not draggable, so rows move around it and it stays put.
 *
 * The note under the heading answers the question archiving raises: what
 * happens to the contacts already set to this option. Nothing does, because
 * what is stored on a contact is the option's key, and that outlives the
 * option's availability. The only change is that nobody new can be given it.
 * Saying both halves here, next to the archived rows, is cheaper than a
 * customer discovering either one by experiment.
 */
export const SingleSelectArchivedDivider: React.FC<SingleSelectArchivedDividerProps> =
  function SingleSelectArchivedDivider({ index }) {
    const { t } = useTranslation();
    // Droppable but not draggable. It has to accept a row dropped on it, since
    // that is how a drag decides the row's section. It must never be picked up
    // itself, which would let the user drag the boundary around and reorder
    // rows by accident.
    const { ref } = useSortable({
      id: ARCHIVED_DIVIDER_ROW_ID,
      index,
      disabled: { draggable: true },
    });

    return (
      <Box
        ref={ref}
        data-test="single-select-archived-divider"
        sx={styles.root}
      >
        <Box sx={styles.heading}>
          <Typography variant="note" color="inherit">
            {t("customField.singleSelect.archivedSectionLabel")}
          </Typography>
        </Box>
        <Typography variant="note" color="inherit">
          {t("customField.singleSelect.archivedSectionNote")}
        </Typography>
      </Box>
    );
  };
